import React, { useState } from "react";
import { Award, Eye } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, Checkbox, FieldError, FormField, Input,
} from "../../ui";
import apiClient from "../../../utils/apiClient";

/**
 * EventCertificateSection — the certificate of participation: whether one is
 * issued, the attendance it takes, and whether it goes out on its own.
 *
 * Attendance is time actually present in the live room, measured against the
 * shorter of the booked slot and how long the room was open — so leaving at
 * the booked end of an overrunning session still counts in full.
 *
 * `sessionId` enables the preview: the real PDF, rendered by the backend with
 * the institute's logo, colours and signatory and a sample name.
 */
const EventCertificateSection = ({ form, update, errors = {}, sessionId, apiBase, ended = false, noun = "webinar" }) => {
  const c = form.certificate;
  const set = (patch) => update({ certificate: { ...c, ...patch } });
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState("");

  const preview = async () => {
    setPreviewing(true);
    setPreviewError("");
    // Opened before the request so the browser does not treat it as a pop-up.
    const tab = window.open("", "_blank");
    try {
      const { data } = await apiClient.get(`${apiBase}/sessions/${sessionId}/certificate-preview.pdf`, {
        responseType: "blob",
        params: { name: "Priya Sharma" },
      });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      if (tab) tab.location.href = url; else window.location.assign(url);
    } catch {
      if (tab) tab.close();
      setPreviewError("The preview could not be rendered. Please try again.");
    }
    setPreviewing(false);
  };

  return (
    <AppCard>
      <AppCardHeader className="tw-flex-row tw-items-start tw-justify-between tw-gap-3">
        <div className="tw-min-w-0">
          <AppCardTitle>Certificate of participation</AppCardTitle>
          <AppCardDescription>
            Issued automatically to attendees who stay long enough, with a unique ID and a public verification page.
          </AppCardDescription>
        </div>
        {sessionId && c.enabled && (
          <AppButton type="button" size="sm" variant="outline" disabled={previewing} onClick={preview}>
            <Eye size={16} /> {previewing ? "Rendering…" : "Preview"}
          </AppButton>
        )}
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
        <Checkbox
          checked={!!c.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
          label={`Issue certificates for this ${noun}`}
          description="Name, event title, date, a certificate ID and a QR code that opens its verification page."
        />

        {c.enabled && (
          <>
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
              <FormField
                label="Minimum attendance"
                error={errors.minAttendancePct}
                hint="Share of the live session someone must be present for."
              >
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={c.minAttendancePct}
                  trailing={<span className="tw-text-xs">%</span>}
                  trailingPad="text"
                  onChange={(e) => set({ minAttendancePct: e.target.value })}
                />
              </FormField>
              <div className="tw-flex tw-items-end">
                <Checkbox
                  checked={c.autoSend !== false}
                  onChange={(e) => set({ autoSend: e.target.checked })}
                  label="Send automatically"
                  description={`About 10 minutes after the ${noun} ends. Untick to review the list and send them yourself.`}
                />
              </div>
            </div>

            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
              <FormField label="Signed by (optional)" hint="Defaults to the signatory in your certificate settings.">
                <Input
                  value={c.signatoryName}
                  maxLength={120}
                  placeholder="Dr. Ananya Rao"
                  onChange={(e) => set({ signatoryName: e.target.value })}
                />
              </FormField>
              <FormField label="Their title (optional)">
                <Input
                  value={c.signatoryTitle}
                  maxLength={120}
                  placeholder="Director"
                  onChange={(e) => set({ signatoryTitle: e.target.value })}
                />
              </FormField>
            </div>

            {ended && (
              <Callout tone="info" icon={<Award size={14} />}>
                This {noun} has ended. Saving a change here re-checks attendance and issues any certificates now earned
                — certificates already issued are never withdrawn by it.
              </Callout>
            )}
            {!sessionId && (
              <p className="tw-m-0 tw-text-xs tw-text-content-subtle">Save the {noun} to preview its certificate.</p>
            )}
            {previewError && <FieldError>{previewError}</FieldError>}
          </>
        )}
      </AppCardContent>
    </AppCard>
  );
};

export default EventCertificateSection;
